import React, { useState, useRef } from 'react';

import _get from 'lodash/get';
import Table from 'cccisd-table';
import Modal from 'cccisd-modal';
import FlagIcon from 'cccisd-icons/flag7';

import AlertsTable from './AlertsTable';
import YouthPlanCard from './YouthPlanCard';
import { getLatestDate, getActivePlan, canReceiveMessages } from '../helpers';

import style from './style.css';

const CARD_BACKGROUND = {
    normal: '#F3F1F2',
    critical: '#EBAEA3',
};

const PRIMARY_DARK = '#503d74';
const FLAG_ICON_COLOR = '#4A2E84';

const YouthCard = ({
    youth,
    youthPlans,
    assignPlanModalRef,
    setYouthSelectionTableSelectedRows,
    setAlertsEditedSignal,
}) => {
    const [opened, setOpened] = useState(false);

    const alertsModalRef = useRef();
    const managePlanModalRef = useRef();

    const flags = _get(youth, 'flags', []);
    const caregivers = _get(youth, 'caregiverList', []);

    const hasCriticalFlags = flags.some(flag => flag.critical && flag.status === 'flagged');

    const youthCardBackgroundColor = hasCriticalFlags ? CARD_BACKGROUND.critical : CARD_BACKGROUND.normal;

    const youthActivePlan = getActivePlan(youthPlans, youth.pawnId);

    const toggleOpen = () => setOpened(prev => !prev);

    const stopPropagation = e => e.stopPropagation();

    const handleAssignPlan = async () => {
        await setYouthSelectionTableSelectedRows([youth.pawnId.toString()]);
        assignPlanModalRef.current.open();
    };

    const openAlertsModal = () => {
        alertsModalRef.current.open();
    };

    const renderPlanDetails = (settings, lastMessageDate) => (
        <div className={style.youth_card_plan_details}>
            <div className={style.youth_card_label}>
                Active Plan: <span className={style.youth_card_field_info}>{settings.planName}</span>
            </div>

            <div className={style.youth_card_label}>
                Auto Renews: <span className={style.youth_card_field_info}>{settings.autoRenew ? 'Yes' : 'No'}</span>
            </div>

            {settings.autoRenew ? (
                <div className={style.youth_card_label}>
                    Start Date: <span className={style.youth_card_field_info}>{settings.startDate}</span>
                </div>
            ) : (
                <div className={style.youth_card_label}>
                    Last Message Scheduled: <span className={style.youth_card_field_info}>{lastMessageDate}</span>
                </div>
            )}
        </div>
    );

    const renderActivePlan = () => {
        const settings = JSON.parse(youthActivePlan.settings);
        const lastMessageDate = getLatestDate(youthActivePlan.sessionList.map(s => s.opensDate));

        return (
            <div className={style.youth_card_plan_grid}>
                <Modal
                    trigger={
                        <button type="button" className="btn btn-dark">
                            Manage Plan
                        </button>
                    }
                    title={`${youth.firstName || ''} ${youth.lastName || ''} - Plan Details`}
                    size="65vw"
                    ref={managePlanModalRef}
                    render={() => (
                        <div className={style.youth_plans_list_container}>
                            {youthPlans[youth.pawnId].map((plan, i) => (
                                <YouthPlanCard key={i} plan={plan} youth={youth} />
                            ))}
                        </div>
                    )}
                />

                {renderPlanDetails(settings, lastMessageDate)}
            </div>
        );
    };

    const renderAssignPlanButton = () => (
        <div className={style.youth_card_center_flex}>
            <button type="button" className="btn btn-dark" onClick={handleAssignPlan}>
                Assign New Plan
            </button>
        </div>
    );

    const renderIneligibleMessage = () => (
        <div className={style.youth_card_right_flex}>
            <div className={style.youth_card_ineligible}>Not eligible for new plan. Cannot receive messages.</div>
        </div>
    );

    const renderPlanSection = () => {
        if (youthActivePlan) {
            return renderActivePlan();
        }

        if (canReceiveMessages(youth)) {
            return renderAssignPlanButton();
        }

        return renderIneligibleMessage();
    };

    const renderCaregivers = () => {
        if (!caregivers.length) {
            return null;
        }

        if (caregivers.length === 1) {
            const caregiver = caregivers[0].fields;
            return (
                <span className={style.youth_card_field_info}>
                    {`${caregiver.firstName} ${caregiver.lastName} (${caregiver.wrapId})`}
                </span>
            );
        }

        return (
            <ul className={`${style.youth_card_field_info} ${style.caregiver_list}`} onClick={stopPropagation}>
                {caregivers.map(caregiver => (
                    <li key={caregiver.fields.wrapId}>
                        {`${caregiver.fields.firstName} ${caregiver.fields.lastName} (${caregiver.fields.wrapId})`}
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div
            className={style.youth_card_container}
            style={{ background: youthCardBackgroundColor }}
            onClick={toggleOpen}
        >
            <span
                className={style.plan_card_arrow}
                style={{
                    transform: opened ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
            >
                ▶
            </span>

            <span className={style.youth_card_name}>{`${youth.firstName || ''} ${youth.lastName || ''}`}</span>

            <div className={`${style.youth_card_info_container} ${style.youth_card_info_padding}`}>
                <div className={style.youth_card_grid}>
                    <div>
                        <span className={style.youth_card_label}>WrapID:</span>{' '}
                        {youth.wrapId && (
                            <span className={style.youth_card_field_info} onClick={stopPropagation}>
                                {youth.wrapId}
                            </span>
                        )}
                    </div>

                    <div>
                        <span className={style.youth_card_label}>
                            {caregivers.length > 1 ? 'Caregivers' : 'Caregiver'}:
                        </span>{' '}
                        {renderCaregivers()}
                    </div>
                </div>

                {flags.length > 0 && (
                    <div className={style.alerts_anchor} onClick={stopPropagation}>
                        <Modal
                            trigger={
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    style={{ color: PRIMARY_DARK, marginRight: '10px' }}
                                >
                                    <u>Review Alerts</u>
                                </button>
                            }
                            ref={alertsModalRef}
                            title="Review Alerts"
                            size="95vw"
                            render={() => (
                                <AlertsTable
                                    orderBy="critical"
                                    youth={youth}
                                    setAlertsEditedSignal={setAlertsEditedSignal}
                                />
                            )}
                        />

                        {flags.some(f => !f.critical && f.status === 'flagged') && (
                            <span style={{ color: FLAG_ICON_COLOR }}>
                                <FlagIcon />
                            </span>
                        )}

                        {flags.some(f => f.critical && f.status === 'flagged') && (
                            <span className={style.flag_critical}>
                                <FlagIcon />
                            </span>
                        )}
                    </div>
                )}

                <div onClick={stopPropagation}>{renderPlanSection()}</div>
            </div>

            {opened && (
                <div className={style.youth_card_expanded_wrapper}>
                    <div className={style.youth_card_expanded_inner}>
                        <div className={style.youth_card_body_container} onClick={stopPropagation}>
                            <div>
                                <div className={style.flex_between}>
                                    <div>
                                        <div>
                                            <span className={style.youth_card_label}>Language:</span>{' '}
                                            {youth.language && youth.language === 'en' ? 'English' : 'Spanish'}
                                        </div>
                                        <div>
                                            <span className={style.youth_card_label}>Phone Number:</span>{' '}
                                            {youth.phoneNumber}
                                        </div>
                                        <div>
                                            <span className={style.youth_card_label}>Receives Messages:</span>{' '}
                                            {youth.receiveMessages ? 'Yes' : 'No'}
                                        </div>
                                    </div>
                                    <div>
                                        <div>
                                            <span className={style.youth_card_label}>Caregiver Phone:</span>{' '}
                                            {youth.caregiverList.length === 1 && (
                                                <span>{youth.caregiverList[0].fields.phoneNumber}</span>
                                            )}
                                            {youth.caregiverList.length > 1 && (
                                                <div>
                                                    <ul>
                                                        {youth.caregiverList.map(caregiver => {
                                                            return (
                                                                <li>{`${caregiver.fields.firstName} ${caregiver.fields.lastName}: ${caregiver.fields.phoneNumber}`}</li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <span className={style.youth_card_label}>Caregiver Receives Messages:</span>{' '}
                                            {youth.caregiverList.length === 1 && (
                                                <span>
                                                    {youth.caregiverList[0].fields.receiveMessages ? 'Yes' : 'No'}
                                                </span>
                                            )}
                                            {youth.caregiverList.length > 1 && (
                                                <div>
                                                    <ul>
                                                        {youth.caregiverList.map(caregiver => {
                                                            return (
                                                                <li>{`${caregiver.fields.firstName} ${
                                                                    caregiver.fields.lastName
                                                                }: ${
                                                                    caregiver.fields.receiveMessages ? 'Yes' : 'No'
                                                                }`}</li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={style.section_divider} />
                                <div>
                                    <br />
                                    <Table
                                        rowKey="pawnId"
                                        columns={[
                                            {
                                                name: 'role',
                                                label: 'Who',
                                                sort: true,
                                                filter: true,
                                            },
                                            {
                                                name: 'wrapId',
                                                label: 'WrapId',
                                                sort: true,
                                                filter: true,
                                            },
                                            {
                                                name: 'planName',
                                                label: 'Plan',
                                                sort: true,
                                                filter: true,
                                            },
                                            {
                                                name: 'startDate',
                                                label: 'Start Date',
                                                sort: true,
                                                filter: true,
                                            },
                                            {
                                                name: 'completedDate',
                                                label: 'Plan Stop Date',
                                                sort: true,
                                                filter: true,
                                                render: completedDate => {
                                                    return completedDate;
                                                },
                                            },
                                            {
                                                name: 'sessions',
                                                label: 'Plan Response Rate',
                                                sort: true,
                                                filter: true,
                                                render: (sessions, row) => {
                                                    let open = sessions.filter(
                                                        session => session.opensDaysAgo > 0
                                                    ).length;

                                                    const completed = sessions.filter(
                                                        session => session.completed
                                                    ).length;

                                                    return (
                                                        <div>{`${completed}/${open} (${
                                                            open === 0 ? '0' : `${Math.round((completed / open) * 100)}`
                                                        }%)`}</div>
                                                    );
                                                },
                                            },
                                            {
                                                name: 'alerts',
                                                label: 'Alerts',
                                                sort: true,
                                                filter: true,
                                                render: (alerts, row) => {
                                                    const button = alerts ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link"
                                                            style={{ color: PRIMARY_DARK }}
                                                            onClick={() => {
                                                                openAlertsModal();
                                                            }}
                                                        >
                                                            <u>{alerts}</u>
                                                        </button>
                                                    ) : (
                                                        alerts
                                                    );

                                                    return <div>{button}</div>;
                                                },
                                            },
                                            {
                                                name: 'notifications',
                                                label: 'Notices',
                                                sort: true,
                                                filter: true,
                                                render: (notifications, row) => {
                                                    const button = notifications ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link"
                                                            style={{ color: PRIMARY_DARK }}
                                                            onClick={() => {
                                                                openAlertsModal();
                                                            }}
                                                        >
                                                            <u>{notifications}</u>
                                                        </button>
                                                    ) : (
                                                        notifications
                                                    );

                                                    return <div>{button}</div>;
                                                },
                                            },
                                            {
                                                name: 'comments',
                                                label: 'Comments',
                                                sort: true,
                                                filter: true,
                                                render: (comments, row) => {
                                                    const button =
                                                        comments.length > 0 ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link"
                                                                style={{
                                                                    color: PRIMARY_DARK,
                                                                }}
                                                                onClick={() => {
                                                                    openAlertsModal();
                                                                }}
                                                            >
                                                                <u>{comments.length > 0 ? 'Yes' : 'No'}</u>
                                                            </button>
                                                        ) : (
                                                            'No'
                                                        );

                                                    return <div>{button}</div>;
                                                },
                                            },
                                        ]}
                                        data={_get(youthPlans, youth.pawnId, []).flatMap(plan => {
                                            const settings = JSON.parse(plan.settings);
                                            const planName = settings.planName;
                                            const startDate = settings.startDate;
                                            const completedDate = plan.completedDate;

                                            const sessionsByIdMap = {};

                                            plan.sessionList.forEach(session => {
                                                const pawnId = session.sessionDeploymentInfo.pawnId;

                                                if (!sessionsByIdMap[pawnId]) {
                                                    sessionsByIdMap[pawnId] = [];
                                                }

                                                sessionsByIdMap[pawnId].push({
                                                    ...session,
                                                    role: session.deployment.role,
                                                    completedDate,
                                                });
                                            });

                                            const sessionsById = Object.entries(sessionsByIdMap).map(
                                                ([pawnId, sessions]) => {
                                                    return {
                                                        pawnId: Number(pawnId),
                                                        sessions,
                                                        role: sessions[0].role,
                                                        planName,
                                                        startDate,
                                                        wrapId: plan.wrapIds.find(wrapIdInfo => {
                                                            return (
                                                                wrapIdInfo.pawnId ===
                                                                sessions[0].sessionDeploymentInfo.pawnId
                                                            );
                                                        }).wrapId,
                                                        completedDate,
                                                        alerts: _get(youth, 'flags', []).filter(
                                                            flag =>
                                                                flag.critical &&
                                                                flag.status === 'flagged' &&
                                                                flag.respondent_pawn_id === Number(pawnId)
                                                        ).length,
                                                        notifications: _get(youth, 'flags', []).filter(flag => {
                                                            return (
                                                                !flag.critical &&
                                                                flag.status === 'flagged' &&
                                                                flag.respondent_pawn_id === Number(pawnId)
                                                            );
                                                        }).length,
                                                        comments: _get(youth, 'flags', []).filter(
                                                            flag =>
                                                                flag.status === 'flagged' &&
                                                                flag.notes &&
                                                                flag.respondent_pawn_id === Number(pawnId)
                                                        ),
                                                    };
                                                }
                                            );

                                            return sessionsById;
                                        })}
                                        showRowActions={false}
                                        hideShowingRowsSummary
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default YouthCard;
